<template>
	<div class="form-base">
		<v-row>
			<v-col cols="4">
				<v-text-field
					label="Фамилия"
					outlined
					v-model.trim="v.lastName.$model"
					:errorMessages="getValidationErrorText(v.lastName)"
					:disabled="disabled"
					@blur="v.lastName.$touch"
				/>
			</v-col>
			<v-col cols="4">
				<v-text-field
					label="Имя"
					outlined
					v-model.trim="v.firstName.$model"
					:errorMessages="getValidationErrorText(v.firstName)"
					:disabled="disabled"
					@blur="v.firstName.$touch"
				/>
			</v-col>
			<v-col cols="4">
				<v-text-field
					label="Отчество (при наличии)"
					outlined
					v-model.trim="v.patronymic.$model"
					:errorMessages="getValidationErrorText(v.patronymic)"
					:disabled="disabled"
					@blur="v.patronymic.$touch"
				/>
			</v-col>
			<v-col cols="6">
				<DateFieldCustom
					label="Дата рождения"
					:value="v.birthDate.$model"
					@change="v.birthDate.$model = $event"
					:errorMessage="v.birthDate"
					:max="new Date().toISOString()"
					:disabled="disabled"
					@blur="v.birthDate.$touch"
				/>
			</v-col>
			<v-col cols="6">
				<v-select
					v-model="v.gender.$model"
					:items="genders"
					:error-messages="getValidationErrorText(v.gender)"
					:disabled="disabled"
					label="Пол"
					outlined
					@blur="v.gender.$touch"
				/>
			</v-col>
			<v-col cols="12">
				<AddressStructuredField
					:value="v.address.$model"
					@change="v.address.$model = $event"
					:v="v.address"
					label="Место жительства или место пребывания"
					:notice-recovery="true"
					:global-disabled="disabled"
					@blur="v.address.$touch"
					:translationKey="translationKey ? `${translationKey}.addr` : undefined"
				/>
			</v-col>
			<v-col cols="12">
				<v-text-field
					v-model="v.birthPlace.$model"
					:errorMessages="getValidationErrorText(v.birthPlace)"
					:disabled="disabled"
					label="Место рождения"
					outlined
					@blur="v.birthPlace.$touch"
				/>
			</v-col>
			<v-col cols="3">
				<v-text-field
					v-model="v.snils.$model"
					:errorMessages="getValidationErrorText(v.snils)"
					:disabled="disabled"
					label="СНИЛС"
					v-mask="['###-###-### ##']"
					outlined
					@blur="v.snils.$touch"
				/>
			</v-col>
			<v-col cols="3">
				<v-text-field
					v-model="v.inn.$model"
					:errorMessages="getValidationErrorText(v.inn)"
					:disabled="disabled"
					label="ИНН"
					v-mask="['############']"
					outlined
					@blur="v.inn.$touch"
				/>
			</v-col>
		</v-row>

		<IdentityDocument :v="v.identityDocument" :disabled="disabled"/>

		<v-divider class="divider"/>

		<JobPlace :v="v" :disabled="disabled"/>
	</div>
</template>

<script>
import {
	and,
	maxLength,
	minLength,
	required,
	requiredIf,
} from 'vuelidate/lib/validators';
import deepmerge from 'deepmerge';

import DateFieldCustom from '@/components/base/DateFieldCustom';
import AddressStructuredField, {
	addressValidate,
} from '@/components/base/AddressStructuredField';
import {
	DATE_FORMAT,
	DATE_FUTURE,
	DATE_PAST,
	EMAIL,
	getValidationErrorText,
	MAX_LENGTH,
	MAX_LENGTH_EMAIL,
	MinLength,
	EMOJI,
	INN,
	KPP,
	PERSON_NAME, TRAILING_AND_LEADING_SPACES,
} from '@/utils/validate';
import {JobPlaceDictionary, Gender} from '@/utils/dictionaries';
import JobPlace from './JobPlace';
import IdentityDocument from './IdentityDocument';

export const personModel = {
	lastName: '',
	firstName: '',
	patronymic: '',
	birthDate: '',
	gender: '',
	birthPlace: '',
	address: {},
	snils: '',
	inn: '',
	isBusinessman: false,
	ogrn: '',
	regDate: '',
	regPlace: '',
	emailUl: '',
	jobPlaceSelect: '',
	identityDocument: {
		code: '21', // default
		number: '',
		series: '',
		docDate: '',
		issuer: '',
		issuerCode: '',
	},
	jobPlace: {
		[JobPlaceDictionary.ORGANIZATION]: {
			orgName: '',
			address: {},
			addressesMatch: false,
			factAddress: {},
			ogrn: '',
			inn: '',
			kpp: '',
		},
		[JobPlaceDictionary.BUSINESSMAN]: {
			fio: '',
			address: {},
			ogrn: '',
			inn: '',
		},
	},
};

export const getPersonValidation = (person) => {
	let personValidation = {
		lastName: {
			required,
			maxLength: maxLength(MAX_LENGTH),
			PERSON_NAME,
			TRAILING_AND_LEADING_SPACES
		},
		firstName: {
			required,
			maxLength: maxLength(MAX_LENGTH),
			PERSON_NAME,
			TRAILING_AND_LEADING_SPACES
		},
		patronymic: {
			maxLength: maxLength(MAX_LENGTH),
			PERSON_NAME,
			TRAILING_AND_LEADING_SPACES
		},
		birthDate: {
			required,
			DATE: and(DATE_FORMAT, DATE_FUTURE, DATE_PAST),
		},
		gender: {
			required,
		},
		birthPlace: {
			required,
			maxLength: maxLength(100),
			EMOJI,
			TRAILING_AND_LEADING_SPACES
		},
		address: {
			required,
			...addressValidate(),
		},
		snils: {
			minLength: minLength(MinLength.SNILS),
		},
		inn: {
			minLength: minLength(12),
			INN: INN(12),
		},
		ogrn: {
			minLength: minLength(15),
		},
		regDate: {
			DATE: and(DATE_FORMAT, DATE_FUTURE, DATE_PAST),
		},
		regPlace: {
			maxLength: maxLength(150),
			EMOJI,
			TRAILING_AND_LEADING_SPACES
		},
		emailUl: {
			EMAIL,
			maxLength: maxLength(MAX_LENGTH_EMAIL),
			TRAILING_AND_LEADING_SPACES
		},
		jobPlaceSelect: {
			required,
		},
		jobPlace: {},
		identityDocument: {
			code: {
				required,
			},
			number: {
				maxLength: maxLength(25),
				EMOJI,
				required,
				TRAILING_AND_LEADING_SPACES
			},
			series: {
				maxLength: maxLength(25),
				EMOJI,
				required: requiredIf((vm) => vm.code === '21'),
				TRAILING_AND_LEADING_SPACES
			},
			docDate: {
				required,
				DATE: and(DATE_FORMAT, DATE_FUTURE, DATE_PAST),
			},
			issuer: {
				required,
				maxLength: maxLength(300),
				EMOJI,
				TRAILING_AND_LEADING_SPACES
			},
			issuerCode: {
				required,
				minLength: minLength(7),
				TRAILING_AND_LEADING_SPACES
			},
		},
	};

	if (person.jobPlaceSelect === JobPlaceDictionary.ORGANIZATION) {
		personValidation.jobPlace[JobPlaceDictionary.ORGANIZATION] = {
			orgName: {
				required,
				maxLength: maxLength(1000),
				EMOJI,
				TRAILING_AND_LEADING_SPACES
			},
			address: {
				required,
				...addressValidate(),
			},
			addressesMatch: {},
			factAddress: {
				required,
				...addressValidate(),
			},
			ogrn: {
				required,
				minLength: minLength(13),
			},
			inn: {
				required,
				minLength: minLength(10),
				INN: INN(10),
			},
			kpp: {
				required,
				minLength: minLength(9),
				KPP,
			},
		};
	}

	if (person.jobPlaceSelect === JobPlaceDictionary.BUSINESSMAN) {
		personValidation.jobPlace[JobPlaceDictionary.BUSINESSMAN] = {
			fio: {
				required,
				maxLength: maxLength(1000),
				EMOJI,
				TRAILING_AND_LEADING_SPACES
			},
			address: {
				required,
				...addressValidate(),
			},
			ogrn: {
				required,
				minLength: minLength(15),
			},
			inn: {
				required,
				minLength: minLength(12),
				INN: INN(12),
			},
		};
	}

	if (person.jobPlaceSelect === JobPlaceDictionary.IS_BUSINESSMAN) {
		personValidation = deepmerge(personValidation, {
			ogrn: {
				required,
			},
			regDate: {
				required,
			},
			regPlace: {
				required,
				EMOJI,
				TRAILING_AND_LEADING_SPACES
			},
			emailUl: {
				required,
			},
		});
	}

	return personValidation;
};

export default {
	name: 'Person',
	props: {
		v: Object,
		disabled: Boolean,
		translationKey: String,
	},
	components: {
		DateFieldCustom,
		JobPlace,
		IdentityDocument,
		AddressStructuredField,
	},
	data: () => ({
		getValidationErrorText,
	}),
	computed: {
		genders() {
			return Object.entries(Gender).map(([value, text]) => ({value, text}));
		},
	},
};
</script>

<style scoped></style>
